import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Badge,
  FloatingLabel
} from "react-bootstrap";
import axios from "axios";
import { API_ENDPOINTS } from '../config/api';
import ProgramPGYUtils from '../utils/programPGYUtils';
import ExpandableUserSelector from './ExpandableUserSelector';
import { SquarePen, Trash2, Plus } from 'lucide-react';
import {
  DEFAULT_CONFERENCE_TYPE_OPTIONS,
  normalizeConferenceTypeValue,
  normalizeHexColor,
  toTransparentBadgeStyle
} from '../constants/conferenceTypes';

const ConferenceSchedulingModal = ({ show, onHide, onConferenceCreated, academicYear }) => {
  const [formData, setFormData] = useState({
    title: "",
    type: "mm",
    academicYear: academicYear,
    dayOfWeek: "monday",
    startTime: "08:00",
    endTime: "09:00",
    startDate: "",
    endDate: "",
    isRecurring: false,
    recurrence: "weekly",
    recurrenceInterval: 1,
    recurrenceEndDate: "",
    requiredAttendees: [],
    location: "",
    zoomLink: ""
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [conferenceTypes, setConferenceTypes] = useState(DEFAULT_CONFERENCE_TYPE_OPTIONS);
  const [showTypeManager, setShowTypeManager] = useState(false);
  const [showTypeEditor, setShowTypeEditor] = useState(false);
  const [editingTypeValue, setEditingTypeValue] = useState("");
  const [typeForm, setTypeForm] = useState({ name: "", abbreviation: "", color: "#0d6efd" });
  const [typeError, setTypeError] = useState("");
  
  // PGY level configuration
  const [pgyLevelOptions, setPgyLevelOptions] = useState([]);
  const [maxPGYLevel, setMaxPGYLevel] = useState(5);
  const [programDetails, setProgramDetails] = useState(null);
  const [programLoading, setProgramLoading] = useState(false);

  // Fetch program details and PGY levels
  const fetchProgramDetails = async () => {
    setProgramLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(`${API_ENDPOINTS.PROGRAMS}/my-program-details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Program details received:', data);
        setProgramDetails(data);
        
        // Fetch PGY levels for this program
        if (data.id) {
          console.log('Fetching PGY levels for programId:', data.id);
          await fetchPGYLevels(data.id);
        } else {
          console.log('No programId found in program details');
        }
      }
    } catch (err) {
      console.error("Failed to fetch program details", err);
    } finally {
      setProgramLoading(false);
    }
  };

  // Fetch PGY level options for the program
  const fetchPGYLevels = async (programId) => {
    try {
      console.log('Fetching PGY levels for program:', programId);
      const options = await ProgramPGYUtils.getPGYLevelOptions(programId);
      console.log('Received PGY options:', options);
      setPgyLevelOptions(options);
      setMaxPGYLevel(options.length > 0 ? options[options.length - 1].value : 5);
    } catch (error) {
      console.error('Error fetching PGY levels:', error);
      // Fallback to default 5 levels
      const fallbackOptions = Array.from({ length: 5 }, (_, i) => ({
        value: i + 1,
        label: `PGY-${i + 1}`
      }));
      console.log('Using fallback PGY options:', fallbackOptions);
      setPgyLevelOptions(fallbackOptions);
      setMaxPGYLevel(5);
    }
  };

  // Fetch users for attendee selection
  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const token = localStorage.getItem('token');
      console.log('Fetching users with token:', token ? 'Present' : 'Missing');
      
      const response = await axios.get(`${API_ENDPOINTS.USERS}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      console.log('Fetched users:', response.data); // Debug log
      const userData = Array.isArray(response.data) ? response.data : [];
      setUsers(userData);
      
      // Also fetch program details and PGY levels
      await fetchProgramDetails();
    } catch (error) {
      console.error("Error fetching users:", error);
      console.error("Error response:", error.response?.data); // Debug log
      setUsers([]); // fallback to empty array on error
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchConferenceTypePreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_ENDPOINTS.CONFERENCES}/type-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const types = Array.isArray(response.data?.types) && response.data.types.length > 0
        ? response.data.types
        : DEFAULT_CONFERENCE_TYPE_OPTIONS;
      setConferenceTypes(types);
      setFormData(prev => ({
        ...prev,
        type: types.some(t => t.value === prev.type) ? prev.type : types[0].value
      }));
    } catch (err) {
      console.error("Failed to fetch conference type preferences", err);
      setConferenceTypes(DEFAULT_CONFERENCE_TYPE_OPTIONS);
    }
  };

  // Helper to get academic year range string
  const getAcademicYearRange = (year) => `${year}-${year + 1}`;

  // When academicYear changes, update startDate and endDate
  useEffect(() => {
    if (show) {
      fetchUsers();
      fetchConferenceTypePreferences();
      const startDate = `${academicYear}-07-01`;
      const endDate = `${parseInt(academicYear) + 1}-06-30`;
      const recurrenceEndDate = `${parseInt(academicYear) + 1}-06-30`; // Last day of academic year
      setFormData(prev => ({
        ...prev,
        academicYear,
        startDate,
        endDate,
        recurrenceEndDate
      }));
    }
  }, [show, academicYear]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveConferenceTypes = async (nextTypes) => {
    const token = localStorage.getItem('token');
    const response = await axios.put(
      `${API_ENDPOINTS.CONFERENCES}/type-preferences`,
      { types: nextTypes },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const savedTypes = Array.isArray(response.data?.types) && response.data.types.length > 0
      ? response.data.types
      : DEFAULT_CONFERENCE_TYPE_OPTIONS;
    setConferenceTypes(savedTypes);
    setFormData(prev => ({
      ...prev,
      type: savedTypes.some(t => t.value === prev.type) ? prev.type : savedTypes[0].value
    }));
  };

  const resetTypeForm = () => {
    setTypeForm({ name: "", abbreviation: "", color: "#0d6efd" });
    setEditingTypeValue("");
    setShowTypeEditor(false);
    setTypeError("");
  };

  const startEditType = (type) => {
    setShowTypeEditor(true);
    setEditingTypeValue(type.value);
    setTypeForm({
      name: type.name || "",
      abbreviation: type.abbreviation || "",
      color: normalizeHexColor(type.color, "#0d6efd")
    });
    setTypeError("");
  };

  const startAddType = () => {
    setShowTypeEditor(true);
    setEditingTypeValue("");
    setTypeForm({ name: "", abbreviation: "", color: "#0d6efd" });
    setTypeError("");
  };

  const handleSaveType = async () => {
    const name = typeForm.name.trim();
    const abbreviation = typeForm.abbreviation.trim();
    const color = normalizeHexColor(typeForm.color);
    const value = normalizeConferenceTypeValue(name);
    if (!name || !abbreviation) {
      setTypeError("Type name and badge abbreviation are required.");
      return;
    }
    if (!value) {
      setTypeError("Please enter a valid type name.");
      return;
    }
    if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
      setTypeError("Please pick a valid color.");
      return;
    }

    const duplicate = conferenceTypes.find(
      (type) => type.value === value && type.value !== editingTypeValue
    );
    if (duplicate) {
      setTypeError("A conference type with this name already exists.");
      return;
    }

    const nextTypes = editingTypeValue
      ? conferenceTypes.map((type) =>
          type.value === editingTypeValue ? { value, name, abbreviation, color } : type
        )
      : [...conferenceTypes, { value, name, abbreviation, color }];

    try {
      await saveConferenceTypes(nextTypes);
      setTypeForm({ name: "", abbreviation: "", color: "#0d6efd" });
      setEditingTypeValue("");
      setShowTypeEditor(false);
      setTypeError("");
    } catch (err) {
      console.error("Failed to save conference types", err);
      setTypeError(err.response?.data?.error || "Failed to save conference types.");
    }
  };

  const handleDeleteType = async (typeValue) => {
    const nextTypes = conferenceTypes.filter((type) => type.value !== typeValue);
    if (nextTypes.length === 0) {
      setTypeError("At least one conference type is required.");
      return;
    }
    try {
      await saveConferenceTypes(nextTypes);
      if (editingTypeValue === typeValue) {
        resetTypeForm();
      }
    } catch (err) {
      console.error("Failed to delete conference type", err);
      setTypeError(err.response?.data?.error || "Failed to delete conference type.");
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.startDate || !formData.endDate) {
      setError("Please fill in all required fields");
      return;
    }

    if (formData.requiredAttendees.length === 0) {
      setError("Please select at least one attendee");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const response = await axios.post(`${API_ENDPOINTS.CONFERENCES}`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setSuccess("Conference scheduled successfully!");
      setTimeout(() => {
        onConferenceCreated();
                 setFormData({
           title: "",
           type: "mm",
           academicYear: academicYear,
           dayOfWeek: "monday",
           startTime: "08:00",
           endTime: "09:00",
           startDate: "",
           endDate: "",
           isRecurring: false,
           recurrence: "weekly",
           recurrenceInterval: 1,
           recurrenceEndDate: `${parseInt(academicYear) + 1}-06-30`,
           requiredAttendees: [],
           location: "",
           zoomLink: ""
         });
        setSuccess("");
      }, 2000);
    } catch (error) {
      console.error("Error creating conference:", error);
      setError(error.response?.data?.error || "Failed to create conference");
    } finally {
      setLoading(false);
    }
  };

  const renderConferenceTypeOption = (typeOption) => {
    const isSelected = formData.type === typeOption.value;
    return (
      <div
        key={typeOption.value}
        className={`p-3 border rounded cursor-pointer ${isSelected ? 'border-primary bg-light' : 'border-secondary'}`}
        onClick={() => handleInputChange({ target: { name: 'type', value: typeOption.value } })}
        style={{ cursor: 'pointer' }}
      >
        <div className="d-flex align-items-center">
          <input
            type="radio"
            name="type"
            value={typeOption.value}
            checked={isSelected}
            onChange={() => {}}
            className="me-2"
          />
          <div className="d-flex flex-row align-items-center gap-2">
            <h6 className="mb-0 fw-semibold">{typeOption.name}</h6>
            <span
              className="badge rounded-pill"
              style={toTransparentBadgeStyle(normalizeHexColor(typeOption.color))}
            >
              {typeOption.abbreviation || typeOption.name}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderUserSelection = () => {
    if (usersLoading) {
      return <div className="text-center text-muted py-3">Loading users...</div>;
    }
    
    if (!Array.isArray(users)) {
      return <div className="text-center text-muted py-3">Error loading users</div>;
    }
    
    if (users.length === 0) {
      return <div className="text-center text-muted py-3">No users available</div>;
    }

    return (
      <ExpandableUserSelector
        availableUsers={users}
        selectedUsers={formData.requiredAttendees}
        onSelectionChange={(updatedSelection) =>
          setFormData((prev) => ({ ...prev, requiredAttendees: updatedSelection }))
        }
        pgyLevelOptions={pgyLevelOptions}
        maxPGYLevel={maxPGYLevel}
        helperText="Selected users will be required to attend this conference"
      />
    );
  };

  // Stack Manage Types modal above Schedule Conference (Bootstrap does not support nested modals natively)
  const handleTypeManagerShow = () => {
    const openModals = document.querySelectorAll(".modal.show").length;
    if (openModals === 0) return;

    setTimeout(() => {
      const backdrops = document.querySelectorAll(".modal-backdrop:not(.stacked)");
      if (backdrops.length === 0) return;

      const latestBackdrop = backdrops[backdrops.length - 1];
      const targetModal = document.querySelector(".modal.conference-type-manager-modal");

      // BS 5.3 defaults: backdrop 1050, modal 1055 — bump each stacked layer by 10
      latestBackdrop.style.zIndex = String(1050 + 10 * openModals);
      if (targetModal) {
        targetModal.style.zIndex = String(1055 + 10 * openModals);
      }
      latestBackdrop.classList.add("stacked");
    }, 0);
  };

  const handleTypeManagerExited = () => {
    if (document.querySelectorAll(".modal.show").length > 0) {
      document.body.classList.add("modal-open");
    }
  };

  return (
    <>
    <style>{`
      /* Keep scroll lock correct when stacked modals open/close */
      .modal {
        overflow-y: auto;
      }
    `}</style>
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
    >
      <Modal.Header closeButton className="border-bottom-0 px-4">
        <Modal.Title className="w-50"> 
          <div className="d-flex flex-column justify-content-start align-items-start">
            <span>Schedule Conference</span>
            <div className="text-hl"></div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="px-4">
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
        <Row>
          <Col md={8}>
          
            <Form>
              <Form.Label className="mb-2 fw-semibold fs-6">Basic Information</Form.Label>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Conference Title" className="mb-3">
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter conference title"
                  required
                />
                <Form.Text className="text-muted">
                  Academic year runs from July 1 to June 30 (e.g., 2025-2026)
                </Form.Text>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Academic Year" className="mb-3">
                <Form.Select
                  name="academicYear"
                  value={formData.academicYear}
                  onChange={handleInputChange}
                  required
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <option key={year} value={year}>
                        {getAcademicYearRange(year)}
                      </option>
                    );
                  })}
                </Form.Select>
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
          </Form>

          <Form.Group className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Form.Label className="mb-0 fw-semibold fs-6">Conference Type</Form.Label>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => {
                  setShowTypeManager(true);
                  setTypeError("");
                }}
              >
                <i className="bi bi-gear me-1"></i>
                Manage Types
              </Button>
            </div>
            <Row>
              {conferenceTypes.map((typeOption) => (
                <Col md={6} className="mb-2" key={typeOption.value}>
                  {renderConferenceTypeOption(typeOption)}
                </Col>
              ))}
            </Row>
          </Form.Group>

          <Form>
            <Form.Label className="mb-2 fw-semibold fs-6">Time and Date</Form.Label>
            <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Day of Week" className="mb-3">
                <Form.Select
                  name="dayOfWeek"
                  value={formData.dayOfWeek}
                  onChange={handleInputChange}
                  required
                >
                  <option value="monday">Monday</option>
                  <option value="tuesday">Tuesday</option>
                  <option value="wednesday">Wednesday</option>
                  <option value="thursday">Thursday</option>
                  <option value="friday">Friday</option>
                  <option value="saturday">Saturday</option>
                  <option value="sunday">Sunday</option>
                </Form.Select>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Start Time" className="mb-3">
                <Form.Control
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                  required
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="End Time" className="mb-3">
                <Form.Control
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleInputChange}
                  required
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Start Date" className="mb-3">
                <Form.Control
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  required
                  min={`${formData.academicYear}-07-01`}
                  max={`${parseInt(formData.academicYear) + 1}-06-30`}
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="End Date" className="mb-3">
                <Form.Control
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  required
                  min={`${formData.academicYear}-07-01`}
                  max={`${parseInt(formData.academicYear) + 1}-06-30`}
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
          </Form>
          

          {/* Recurrence Settings */}
          <Form.Group className="mb-3 bg-info rounded-3 p-3">
            <Form.Check
              type="checkbox"
              id="isRecurring"
              name="isRecurring"
              checked={formData.isRecurring}
              onChange={(e) => handleInputChange({ target: { name: 'isRecurring', value: e.target.checked } })}
              label={<div className="d-flex flex-column justify-content-start align-items-start"><span className="fw-semibold"><i className="bi bi-repeat me-1"></i>This is a recurring conference</span>
                <span className="ms-2 small text-info">Conference will repeat on the same day and time each week</span></div>
              }
            />          </Form.Group>
        

          {formData.isRecurring && (
            <Row>
              <Col md={formData.recurrence === 'custom' ?4 : 6}>
                <Form.Group className="mb-md-0 mb-3">
                  <FloatingLabel controlId="floatingInput" label="Recurrence Pattern">
                  <Form.Select
                    name="recurrence"
                    value={formData.recurrence}
                    onChange={handleInputChange}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="custom">Custom</option>
                  </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              {formData.recurrence === 'custom' && (
                <Col md={formData.recurrence === 'custom' ? 4 : 6}>
                  <Form.Group className="mb-md-0 mb-3">
                    <FloatingLabel controlId="floatingInput" label="Custom Interval (weeks)">
                    <Form.Control
                      type="number"
                      name="recurrenceInterval"
                      value={formData.recurrenceInterval}
                      onChange={handleInputChange}
                      min="1"
                      max="52"
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              )}
                 <Col md={formData.recurrence === 'custom' ? 4 : 6}>
                 <Form.Group className="mb-md-0">
                   <FloatingLabel controlId="floatingInput" label="Recurrence End Date">
                   <Form.Control
                     type="date"
                     name="recurrenceEndDate"
                     value={formData.recurrenceEndDate}
                     onChange={handleInputChange}
                     min={formData.startDate}
                     max={`${parseInt(formData.academicYear) + 1}-06-30`}
                     placeholder={`${parseInt(formData.academicYear) + 1}-06-30`}
                   />
                   </FloatingLabel>
                 
                 </Form.Group>  
                 
               </Col>
               <Col md={12} className="mb-3">
               <Form.Text className="text-muted">
                     Defaults to last day of academic year (June 30)
                </Form.Text>
                </Col>
            </Row>
          )}
          <Row>
            <Col md={8}>
              <Form.Group>
                <FloatingLabel controlId="floatingZoomLink" label="Zoom Link (Optional)" className="mb-3">
                  <Form.Control
                    type="url"
                    name="zoomLink"
                    value={formData.zoomLink}
                    onChange={handleInputChange}
                    placeholder="https://zoom.us/j/..."
                  /> 
                </FloatingLabel>
              
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <FloatingLabel controlId="floatingLocation" label="Conference Location" className="mb-3">
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Enter conference location"
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md={12}>
            <Form.Text className="text-muted">
                  Enter the Zoom meeting link and location for this conference. This will be applied to all sessions.
            </Form.Text>
            </Col>

         
          </Row>

          </Col>
          <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>
              <span className="fw-semibold">Required Attendees</span> 
              <span className="text-muted small ms-2">({formData.requiredAttendees.length} selected)</span>
            </Form.Label>
            {renderUserSelection()}
          </Form.Group>
          </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top-0 px-4">
          <Button variant="light" size="sm" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={loading}>
            {loading ? "Creating..." : "Schedule Conference"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
    <Modal
      show={showTypeManager}
      size="lg"
      onHide={() => { setShowTypeManager(false); resetTypeForm(); }}
      onShow={handleTypeManagerShow}
      onExited={handleTypeManagerExited}
      centered
      enforceFocus={false}
      className="conference-type-manager-modal"
    >
      <Modal.Header closeButton className="border-bottom-0 px-4">
        <Modal.Title>
          <div className="d-flex flex-column justify-content-start align-items-start">
            <span>Manage Conference Types</span>
            <div className="text-hl-warning"></div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        {typeError && <Alert variant="danger">{typeError}</Alert>}
        <div className="mb-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h6 className="mb-2">Current Types</h6>
            <Button size="sm" variant="outline-primary" onClick={startAddType}>
              <div className="d-flex align-items-center">
              <Plus style={{ width: 16, height: 16 }}/>
              <span className="d-none d-md-inline ms-1">Add New</span></div>
            </Button>
          </div>
          <Row className="g-2">
            {conferenceTypes.map((type, idx) => (
              <Col xs={12} md={6} key={type.value}>
                <div className="border rounded p-2 d-flex justify-content-between align-items-center h-100">
                  <div className="d-flex align-items-center gap-2">
                    <strong>{type.name}</strong>
                    <span
                      className="badge rounded-pill"
                      style={toTransparentBadgeStyle(normalizeHexColor(type.color))}
                    >
                      {type.abbreviation}
                    </span>
                  </div>
                  <div className="d-flex">
                    <Button
                      size="sm"
                      variant="light-lite"
                      className="p-2 d-flex align-items-center justify-content-center"
                      onClick={() => startEditType(type)}
                    >
                      <SquarePen style={{ width: 16, height: 16 }}/>
                    </Button>
                    <Button
                      size="sm"
                      variant="light-lite"
                      className="p-2 d-flex align-items-center justify-content-center"
                      onClick={() => handleDeleteType(type.value)}
                    >
                      <Trash2 style={{ width: 16, height: 16 }}/>
                    </Button>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
        {showTypeEditor && (
          <>
            <div className=" p-3 bg-light rounded-3">
              <h6 className="mb-3">{editingTypeValue ? "Edit Conference Type" : "Add Conference Type"}</h6>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="Type Name">
                    <Form.Control
                      value={typeForm.name}
                      onChange={(e) => setTypeForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Simulation Lab"
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="Badge Abbreviation">
                    <Form.Control
                      value={typeForm.abbreviation}
                      onChange={(e) => setTypeForm((prev) => ({ ...prev, abbreviation: e.target.value }))}
                      placeholder="e.g. SIM"
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-start justify-content-md-between gap-3">
                <Form.Group className="d-flex flex-row align-items-center gap-3">
                  <Form.Label className="fw-semibold small">Badge Color</Form.Label>
                  <div className="d-flex align-items-center gap-3 bg-white rounded-3">
                    <Form.Control
                      type="color"
                      value={normalizeHexColor(typeForm.color, "#0d6efd")}
                      onChange={(e) => setTypeForm((prev) => ({ ...prev, color: normalizeHexColor(e.target.value, "#0d6efd") }))}
                      style={{ width: 46, height: 36, padding: 2 }}
                    />
                    <div className=" fw-semibold small text-muted pe-3 ">{normalizeHexColor(typeForm.color, "#0d6efd")}</div>
                  </div>
                </Form.Group>
                  <div className="d-flex align-items-center gap-2">
                    <Button variant="light" size="sm" onClick={resetTypeForm}>
                      Cancel
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSaveType}>
                      Save
                    </Button>
                  </div>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="border-top-0 px-4">
        <Button variant="light" size="sm" onClick={() => { setShowTypeManager(false); resetTypeForm(); }}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default ConferenceSchedulingModal; 